import React from 'react';

// §lab-visit-type-disambiguation (INTAKE_PRD_LAB_VISIT_TYPE_DISAMBIGUATION,
// dashboard FE 2026-08-21 — incident visit #49345585).
//
// The Beluga visitType is decided by WHICH ROUTE the patient opens, and it
// cannot be changed after the visit exists:
//
//   /lab-panel     → labTestingAutoAuthorization → orders NEW labs
//   /lab-analysis  → labAnalysis                 → reviews labs they ALREADY have
//
// Two routes, one word apart, with no way to tell them apart on the way in. A
// patient holding a results PDF opened /lab-panel; the wrong visit type was
// created, and it surfaced a week later as a requisition document that was
// never going to arrive. Downstream cannot fix this — by then the Beluga visit
// exists and the only remedy is a second visit under the right type.
//
// So this banner does two things BEFORE submission:
//   §2.1 states the intent in the patient's own language, so someone in the
//        wrong flow can recognise it from the first line they read;
//   §2.2 gives them a one-click route to the other flow, which is what turns an
//        unrecoverable mistake into a recoverable one.

const FLOWS = {
  panel: {
    // What this flow does, in the patient's words.
    intent: "We'll order lab work for you.",
    detail: 'A licensed provider authorizes your panel and you get your blood drawn at a partner lab.',
    // The way out, phrased as the situation the patient is actually in.
    escapeAsk: 'Already have lab results you want reviewed?',
    escapeCta: 'Go to Lab Analysis instead',
    escapeHref: '/lab-analysis',
  },
  analysis: {
    intent: 'Upload lab results you already have, for your provider to review.',
    detail: "You'll share your existing results and a licensed provider reviews them with you.",
    escapeAsk: 'Need new lab work ordered instead?',
    escapeCta: 'Go to Lab Panel instead',
    escapeHref: '/lab-panel',
  },
};

// Params that must survive the hop, and ONLY these.
//
//   t          tenant key — branding + pricing; dropping it silently hands the
//              patient a Fitpal-branded form mid-flow
//   embed      keeps the dashboard iframe contract intact
//   pt         authenticated-patient handoff token — drop it and they're asked
//              to re-identify, and the visit detaches from their account
//   returnUrl  the dashboard page they came from; without it the post-submit
//              return lands somewhere generic
//   email / firstName / lastName / phone
//              prefill; dropping them makes the patient re-enter details they
//              already gave
//   belugaEnv  staging sign-off gate — losing it mid-walk silently switches the
//              test back to the live path
//
// Deliberately NOT carried: masterId, medId, dose and anything else
// treatment-specific. They are meaningless on a lab flow, and blindly forwarding
// the whole query string means any param someone appends rides along into a
// different visit type. Allowlist, not passthrough. (Per dashboard FE,
// 2026-08-21.)
const CARRY_PARAMS = [
  't', 'embed', 'pt', 'returnUrl',
  'email', 'firstName', 'lastName', 'phone',
  'belugaEnv',
];

const withCurrentQuery = (path) => {
  if (typeof window === 'undefined') return path;
  try {
    const current = new URLSearchParams(window.location.search);
    const next = new URLSearchParams();
    CARRY_PARAMS.forEach((k) => {
      const v = current.get(k);
      if (v !== null && v !== '') next.set(k, v);
    });
    const qs = next.toString();
    return qs ? `${path}?${qs}` : path;
  } catch {
    return path;
  }
};

const LabFlowIntent = ({ flow }) => {
  const cfg = FLOWS[flow];
  if (!cfg) return null;
  return (
    <div className="w-full px-4 pt-1 pb-2" data-testid={`lab-flow-intent-${flow}`}>
      <div className="max-w-2xl mx-auto rounded-2xl border border-[#2d8a5f]/25 bg-[#f0faf4] px-4 py-3">
        <p className="text-sm font-semibold text-gray-900" data-testid="lab-flow-intent-line">
          {cfg.intent}
        </p>
        <p className="text-xs text-gray-600 mt-0.5">{cfg.detail}</p>
        <p className="text-xs text-gray-600 mt-2 pt-2 border-t border-[#2d8a5f]/15">
          {cfg.escapeAsk}{' '}
          <a
            href={withCurrentQuery(cfg.escapeHref)}
            data-testid="lab-flow-switch"
            className="font-semibold text-[#2d8a5f] underline underline-offset-2 hover:text-[#236b4a]"
          >
            {cfg.escapeCta}
          </a>
        </p>
      </div>
    </div>
  );
};

export default LabFlowIntent;
